/*
 * A switch: something that takes effect the moment it is thrown.
 *
 * Not a checkbox. A checkbox is an answer that is submitted with a form; a
 * switch is an instruction that lands immediately, which is why it is
 * `role="switch"` with `aria-checked` rather than a restyled checkbox. The
 * distinction matters in this console because the things behind these are
 * consent grants and publication states, and "on" has to mean it is on now.
 *
 * The accessible name never changes with the state. A control that reads "Mute"
 * when it is off and "Unmute" when it is on cannot be asked what it currently
 * is, only inferred from what it offers to do, and the inference is backwards
 * as often as not. The state travels through `aria-checked`, where a screen
 * reader can read it on demand and where a test can assert it.
 *
 * The visible state is carried by the knob's position, not by the track's
 * colour. Position survives greyscale, printing, and the roughly one man in
 * twelve who would otherwise be reading a colour he cannot separate.
 */

import { useId } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { joinIds } from '../aria'
import styles from './Switch.module.css'

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type' | 'children'> {
  /** The name of the setting. Fixed: it must not describe the action. */
  label: ReactNode
  hint?: ReactNode
  checked: boolean
  onChange(next: boolean): void
  labelHidden?: boolean
}

export function Switch({
  label,
  hint,
  checked,
  onChange,
  labelHidden = false,
  className,
  disabled,
  ...rest
}: SwitchProps) {
  const base = useId()
  const labelId = `${base}-label`
  const hintId = `${base}-hint`

  const rootClassName = [styles.row, className].filter(Boolean).join(' ')

  return (
    <div className={rootClassName}>
      <span className={[styles.text, labelHidden ? 'visually-hidden' : ''].filter(Boolean).join(' ')}>
        <span className={styles.label} id={labelId}>
          {label}
        </span>
        {hint ? (
          <span className={styles.hint} id={hintId}>
            {hint}
          </span>
        ) : null}
      </span>
      <button
        {...rest}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={joinIds(rest['aria-labelledby'], labelId)}
        aria-describedby={joinIds(rest['aria-describedby'], hint ? hintId : undefined)}
        className={styles.track}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.knob} />
      </button>
    </div>
  )
}
